import classNames from 'classnames'
import React, { useEffect } from 'react'

import { Button, BUTTON_TYPES, Icon } from '@src/quince-components/src'
import { PartialAddress } from '@src/quince-site-common/src/services/addressService/addressType'
import {
  CheckoutExpressUpdateDetail,
  EnrichedCheckout,
  ShippingMethods,
} from '@src/quince-site-common/src/services/checkout-service-full/checkout-service-full.types'

import { UpdateExpressCompleteDetails } from './apple-pay-button.helpers'
import styles from './apple-pay-button.module.scss'
import ApplePayIcon from './icons/apple-pay.svg'
import { useApplePayBraintree } from './use-apple-pay-braintree.hook'

import type { ApplePayPaymentRequest, ApplePaySession } from 'braintree-web'

export interface ApplePayConfig {
  getCheckoutDetails: (isPreorder?: boolean) => Promise<EnrichedCheckout | null>
  onError?: (error?: unknown) => void
  validateApplePayClick?: () => boolean
  updateShippingAddress: (address: PartialAddress) => Promise<CheckoutExpressUpdateDetail>
  updateShippingOption: (shippingOptionId: ShippingMethods) => Promise<CheckoutExpressUpdateDetail>
  checkoutExpressUpdateDetail: CheckoutExpressUpdateDetail | null
  disableApplePayExpress?: boolean
  updateExpressCompleteDetails: UpdateExpressCompleteDetails
  capture: () => Promise<void>
}

interface ApplePayButtonProps {
  className?: string
  dataSelectorKey?: string
  productPrice?: number
  applePayConfig: ApplePayConfig
  onReady?: () => void
  enableAnimation?: boolean
  disableClick?: boolean
}

declare global {
  interface Window {
    ApplePaySession?: {
      new (version: number, paymentRequest: ApplePayPaymentRequest): ApplePaySession
      supportsVersion?: (version: number) => boolean
      canMakePayments?: () => boolean
      STATUS_SUCCESS: number
      STATUS_FAILURE: number
    }
  }
}

const ApplePayButton = ({
  className,
  dataSelectorKey,
  productPrice,
  applePayConfig,
  onReady,
  enableAnimation,
  disableClick,
}: ApplePayButtonProps) => {
  const {
    getCheckoutDetails,
    onError,
    validateApplePayClick,
    updateShippingAddress,
    updateShippingOption,
    checkoutExpressUpdateDetail,
    updateExpressCompleteDetails,
    capture,
    disableApplePayExpress,
  } = applePayConfig

  const { isApplePayClickInProgress, isSupported, handleApplePayClick } = useApplePayBraintree({
    onError,
    getCheckoutDetails,
    validateApplePayClick,
    updateShippingAddress,
    productPrice,
    updateShippingOption,
    checkoutExpressUpdateDetail,
    updateExpressCompleteDetails,
    capture,
  })

  const isSupportsApplePay = isSupported && !disableApplePayExpress
  const [shouldAnimate, setShouldAnimate] = React.useState(false)

  useEffect(() => {
    if (!isSupportsApplePay || !enableAnimation) {
      return
    }
    const timer = requestAnimationFrame(() => {
      setShouldAnimate(true)
      onReady?.()
    })
    return () => {
      cancelAnimationFrame(timer)
    }
  }, [isSupportsApplePay, onReady, enableAnimation])

  if (!isSupported || disableApplePayExpress) {
    return null
  }

  return (
    <Button
      onClick={handleApplePayClick}
      data-selector-key={dataSelectorKey}
      type={BUTTON_TYPES.SECONDARY}
      className={classNames(
        className,
        styles.applePayButton,
        enableAnimation && styles.applePayButtonAnimated,
        enableAnimation && shouldAnimate && styles.applePayButtonExpanded,
      )}
      showLoader={isApplePayClickInProgress}
      disabled={disableClick || (enableAnimation && !shouldAnimate)}
    >
      <Icon iconSvg={ApplePayIcon} className={styles.applePayIcon} />
    </Button>
  )
}

export default ApplePayButton
